import MomentUtils from '@date-io/moment'
import {
  withWidth,
  WithStyles,
  Typography,
  Grid,
  Button
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import { WithWidth } from '@material-ui/core/withWidth'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import i18n from 'i18next'
import moment from 'moment-timezone'
import { useState, useEffect, useLayoutEffect, FC } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Dispatch } from 'redux'
import { closeDialog } from '../../actions/dialogs'
import { navigate } from '../../actions/routes'
import { CloseDialog } from '../../actions/types'
import { setWindowWidth } from '../../actions/users'
import { pushGTMEvent } from '../../config/googleTagManager'
import customCloseIcon from '../../images/customCloseIcon.svg'
import { objectNotEmpty } from '../../libs/objects'
import { selectDialogParams } from '../../selectors/dialogs'
import { selectIsCreateApplicationRoute } from '../../selectors/router'
import {
  selectIsAdminUser,
  selectUserIsAuthenticated,
  selectUserAttributes
} from '../../selectors/users'
import { UserAttributes } from '../../services/auth/types'
import {
  segmentIdentify,
  segmentLoadKey,
  segmentLogPage
} from '../../services/segment'
import { getCurrentLanguage } from '../../translations/i18n'
import { MuiClasses } from '../../types'
import { StateInterface } from '../../types/states'
import Suggest from '../Apartment/Stepper/Steps/Suggest'
import Dialog from '../Dialog'
import NavBar from './Menus/NavBar'
import navigationStyles from './navigationStyles'
import 'moment/locale/fi'

moment.tz.setDefault('Europe/Helsinki')
const locales = {
  en: 'en',
  fi: 'fi'
}

interface tProps extends WithTranslation {
  t: i18n.TFunction
}

interface PropsFromState {
  isAuthenticated: boolean
  isAdminUser: boolean
  showLanguageSwitcher: boolean
  navigateToPath: any
  userAttributes: any
}

interface DispatchProps {
  doNavigation: (path: string) => void
  setWindowWidth: (windowWidth: string) => void
  closeDialogAction: CloseDialog
}

interface OwnProps extends RouteComponentProps {
  Routes: () => JSX.Element
}

type NavProps = OwnProps &
  DispatchProps &
  PropsFromState &
  WithStyles &
  WithWidth &
  tProps

const SuggestModal = ({
  classes,
  t,
  dialogId,
  closeDialogAction,
  leaveDialogAction
}: {
  classes: MuiClasses
  t: i18n.TFunction
  dialogId: string
  closeDialogAction: CloseDialog
  leaveDialogAction: () => void
}) => {
  return (
    <Grid
      container
      justify='center'
      style={{ maxWidth: '800px', margin: 'auto' }}
    >
      <Typography variant='h5' align='center' className={classes.modalTitle}>
        {t('application.titlePopupLeavingApplicationsPage')}
      </Typography>
      <Grid item sm={12} style={{ margin: '15px 0 20px 0' }}>
        <Suggest
          isModal
          contentMaxWidth='100%'
          containerMaxWidth='100%'
          content={t('application.hints.hintLeavingApplicationsPage')}
          classes={classes}
        />
      </Grid>

      <Grid container spacing={2}>
        <Grid item sm={6} xs={12}>
          <Button
            fullWidth
            className={classes.dialogSaveBtn}
            style={{ textTransform: 'uppercase' }}
            onClick={() => {
              closeDialogAction(dialogId)
            }}
          >
            {t('application.inviteApplicant')}
          </Button>
        </Grid>
        <Grid item sm={6} xs={12}>
          <Button
            variant='contained'
            fullWidth
            className={classes.primaryBtn}
            style={{ textTransform: 'uppercase' }}
            onClick={() => {
              leaveDialogAction()
              closeDialogAction(dialogId)
            }}
          >
            {t('application.leaveAnyway')}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  )
}

/**
 * Takes Routes element from Router.tsx, everything above it is handling navigation
 */
const Navigation: FC<NavProps> = ({
  classes,
  isAuthenticated,
  isAdminUser,
  Routes,
  doNavigation,
  width,
  location: { pathname },
  showLanguageSwitcher,
  setWindowWidth,
  navigateToPath,
  t,
  closeDialogAction,
  userAttributes
}) => {
  useLayoutEffect(() => {
    pushGTMEvent('optimize.activate')
  }, [pathname])

  const [locale, setLocale] = useState<string>(getCurrentLanguage())

  useEffect(() => {
    const currentLocale = getCurrentLanguage()
    setLocale(currentLocale)
  }, [locale, setLocale])

  const isApartmentStepperRoute =
    pathname.startsWith('/create/apartment/steps/') ||
    /^\/users\/[^/]+\/apartments\/[^/]+\/steps\//.test(pathname)
  const shouldShowBottomNav =
    isAuthenticated &&
    typeof window !== 'undefined' &&
    window.innerWidth < 1066 &&
    !isApartmentStepperRoute
  const shouldShowBottomNavSpacer =
    shouldShowBottomNav && !pathname.startsWith('/profile')
  const contentClassName = shouldShowBottomNav
    ? `${classes.content} ${classes.contentWithBottomNav}`
    : classes.content

  useEffect(() => {
    if (isAuthenticated) {
      segmentLoadKey()
      segmentIdentify()
    }
  }, [isAuthenticated])
  useEffect(() => {
    segmentLogPage()
  }, [pathname])
  useEffect(() => {
    setWindowWidth(width)
  }, [width])
  useEffect(() => {
    if (typeof window === 'undefined') return
    const target = document.querySelector('.scrollToTopTarget')
    if (target) {
      ;(target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }
  }, [pathname])
  return (
    <div className={classes.root}>
      <NavBar
        isAuthenticated={isAuthenticated}
        classes={classes}
        doNavigation={doNavigation}
        pathname={pathname}
        isAdminUser={isAdminUser}
        showLanguageSwitcher={showLanguageSwitcher}
        userAttributes={userAttributes}
      />

      <main className={contentClassName}>
        <div className='scrollToTopTarget' />
        <div className={classes.toolbar} />
        <div className={classes.containerHolder}>
          <MuiPickersUtilsProvider
            libInstance={moment}
            utils={MomentUtils}
            locale={locales[locale]}
          >
            <Routes />
          </MuiPickersUtilsProvider>
          {shouldShowBottomNavSpacer && (
            <div className={classes.bottomNavSpacer} />
          )}
        </div>
      </main>

      <Dialog
        buttonVariant='outlined'
        dialogId='stopLeavingFromPageApplication'
        noOpenDialogButton
        Content={
          <SuggestModal
            t={t}
            classes={classes}
            dialogId='stopLeavingFromPageApplication'
            leaveDialogAction={() => {
              doNavigation(navigateToPath)
            }}
            closeDialogAction={closeDialogAction}
          />
        }
        customCloseIcon={customCloseIcon}
        borderRadius='10px'
        maxWidth='md'
      />

      {/* <Dialog
        dialogId='stopLeavingFromPageApplication'
        confirmLabel={t('application.inviteApplicant')}
        closeLabel={t('application.leaveAnyway')}
        noOpenDialogButton
        beforeCloseDialogAction={() => {
          doNavigation(navigateToPath)
        }}
        customCloseIcon={customCloseIcon}
        maxWidth='md'
        borderRadius='20px'
        customButtonContainerClassName={classes.buttonContainerClassName}
        customButtonConfirmClassName={classes.buttonConfirmClassName}
        customButtonCloseClassName={classes.buttonCloseClassName}
        noFreeSpace
        Content={
          <GridContainer className={classes.suggestDialogContainer}>
            <Typography className={classes.titlePopup} variant='h5'>
              {t('application.titlePopupLeavingApplicationsPage')}
            </Typography>
            <Suggest
              containerMaxWidth='100%'
              contentMaxWidth='100%'
              content={t('application.hints.hintLeavingApplicationsPage')}
            />
          </GridContainer>
        }
      /> */}
    </div>
  )
}

const mapStateToProps = (state: StateInterface) => {
  const stateUserAttributes = selectUserAttributes(state)
  return {
    isAuthenticated: selectUserIsAuthenticated(state),
    isAdminUser: selectIsAdminUser(state),
    showLanguageSwitcher: !selectIsCreateApplicationRoute(state),
    navigateToPath: selectDialogParams('stopLeavingFromPageApplication')(state),
    userAttributes: objectNotEmpty<UserAttributes>(stateUserAttributes)
      ? stateUserAttributes
      : undefined
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  doNavigation: (path: string) => dispatch(navigate(path)),
  setWindowWidth: (width: string) => dispatch(setWindowWidth(width)),
  closeDialogAction: (id: string) => dispatch(closeDialog(id))
})

export default withRouter(
  connect<PropsFromState, DispatchProps, OwnProps, StateInterface>(
    mapStateToProps,
    mapDispatchToProps
  )(
    withStyles(navigationStyles, { withTheme: true })(
      withWidth()(withTranslation()(Navigation))
    )
  )
)
